// Every change to a workout is saved on the device first, then queued for upload (sync.ts). Each function takes
// the workout's owner: you, or (for the planner) someone you're logging a workout with.
import { uuidv7, type SetRecord, type SetValues, type User, type WorkoutRecord } from '@squats/shared';
import { db, type LocalSet, type LocalWorkout } from './db.ts';
import { requestSync } from './sync.ts';

/** A timestamp later than the record's last edit, even if the phone's clock jumped back. */
const stamp = (prev?: { updatedAt: number }) => Math.max(Date.now(), (prev?.updatedAt ?? 0) + 1);

/** Whose workout it is: its owner's id, and the units their sets are recorded in. */
export type Owner = Pick<User, 'id' | 'units'>;

async function editWorkout(user: Owner, id: string, change: (w: LocalWorkout) => Partial<WorkoutRecord>) {
  await db.transaction('rw', db.workouts, async () => {
    const w = await db.workouts.get(id);
    if (!w || w.userId !== user.id) throw new Error('Not your workout');
    await db.workouts.put({ ...w, ...change(w), updatedAt: stamp(w), dirty: 1, rejected: undefined });
  });
  requestSync();
}

async function editSet(user: Owner, id: string, change: Partial<SetRecord>) {
  await db.transaction('rw', db.sets, async () => {
    const s = await db.sets.get(id);
    if (!s || s.userId !== user.id) throw new Error('Not your set');
    await db.sets.put({ ...s, ...change, updatedAt: stamp(s), dirty: 1, rejected: undefined });
  });
  requestSync();
}

type NewWorkout = { date: string; splitId: string | null; name: string; exerciseIds?: string[] };
const fresh = (owner: Pick<User, 'id'>, w: NewWorkout, now: number, sessionId: string | null = null): LocalWorkout => ({
  id: uuidv7(now),
  ...w,
  exerciseIds: [...new Set(w.exerciseIds ?? [])],
  notes: null,
  startedAt: now,
  endedAt: null,
  updatedAt: now,
  deletedAt: null,
  sessionId,
  userId: owner.id,
  dirty: 1,
});

/** A new workout, already listing the day's planned exercises if there are any. */
export async function startWorkout(user: Owner, w: NewWorkout): Promise<string> {
  const workout = fresh(user, w, Date.now());
  await db.workouts.add(workout);
  requestSync();
  return workout.id;
}

/**
 * The planner's workout plus one for each training partner, done together: the same exercises, linked by a
 * session so one screen logs everyone's sets. (Each partner's split is their own plan for the day, if any.)
 */
export async function startSession(user: Owner, w: NewWorkout, partners: { id: string; splitId: string | null }[]): Promise<string> {
  if (!partners.length) return startWorkout(user, w);
  const now = Date.now();
  const mine = fresh(user, w, now);
  mine.sessionId = mine.id;
  await db.workouts.bulkAdd([mine, ...partners.map((p) => fresh(p, { ...w, splitId: p.splitId }, now, mine.id))]);
  requestSync();
  return mine.id;
}

/** Adds someone to a workout already under way, with the same exercises (e.g. they turned up late). */
export async function joinSession(person: Pick<User, 'id'>, workout: WorkoutRecord): Promise<void> {
  const sessionId = workout.sessionId ?? workout.id;
  await db.transaction('rw', db.workouts, async () => {
    if (!workout.sessionId) {
      const w = await db.workouts.get(workout.id);
      if (w) await db.workouts.put({ ...w, sessionId, updatedAt: stamp(w), dirty: 1 });
    }
    await db.workouts.add(fresh(person, { date: workout.date, splitId: null, name: workout.name, exerciseIds: workout.exerciseIds }, Date.now(), sessionId));
  });
  requestSync();
}

export const addExercise = (user: Owner, workoutId: string, exerciseId: string) =>
  editWorkout(user, workoutId, (w) => ({ exerciseIds: w.exerciseIds.includes(exerciseId) ? w.exerciseIds : [...w.exerciseIds, exerciseId] }));

/** Takes an exercise out of the workout (only offered once it has no sets). */
export const removeExercise = (user: Owner, workoutId: string, exerciseId: string) =>
  editWorkout(user, workoutId, (w) => ({ exerciseIds: w.exerciseIds.filter((id) => id !== exerciseId) }));

export const setNotes = (user: Owner, workoutId: string, notes: string) => editWorkout(user, workoutId, () => ({ notes: notes.trim() || null }));
export const renameWorkout = (user: Owner, workoutId: string, name: string) => editWorkout(user, workoutId, (w) => ({ name: name.trim().slice(0, 60) || w.name }));
export const finishWorkout = (user: Owner, workoutId: string) => editWorkout(user, workoutId, () => ({ endedAt: Date.now() }));
export const reopenWorkout = (user: Owner, workoutId: string) => editWorkout(user, workoutId, () => ({ endedAt: null }));

/** Deletes the workout and its sets (kept as deleted records until the server has them). */
export async function deleteWorkout(user: Owner, workoutId: string) {
  const now = Date.now();
  await db.transaction('rw', db.workouts, db.sets, async () => {
    const w = await db.workouts.get(workoutId);
    if (!w || w.userId !== user.id) throw new Error('Not your workout');
    await db.workouts.put({ ...w, deletedAt: now, updatedAt: stamp(w), dirty: 1 });
    const sets = await db.sets.where('workoutId').equals(workoutId).toArray();
    for (const s of sets) if (!s.deletedAt) await db.sets.put({ ...s, deletedAt: now, updatedAt: stamp(s), dirty: 1 });
  });
  requestSync();
}

export async function logSet(
  user: Owner,
  workoutId: string,
  exerciseId: string,
  values: SetValues,
  extra: { warmup: boolean },
): Promise<LocalSet> {
  const now = Date.now();
  const set: LocalSet = {
    id: uuidv7(now),
    workoutId,
    exerciseId,
    ...values,
    unit: user.units,
    ...extra,
    // RPE isn't asked for any more; older sets keep theirs.
    rpe: null,
    loggedAt: now,
    updatedAt: now,
    deletedAt: null,
    userId: user.id,
    dirty: 1,
  };
  await db.transaction('rw', db.workouts, db.sets, async () => {
    await db.sets.add(set);
    // Logging a set also lists its exercise in the workout.
    const w = await db.workouts.get(workoutId);
    if (w && !w.exerciseIds.includes(exerciseId)) await db.workouts.put({ ...w, exerciseIds: [...w.exerciseIds, exerciseId], updatedAt: stamp(w), dirty: 1 });
  });
  requestSync();
  return set;
}

/** Saving an edited set records it in the person's current units. */
export const updateSet = (user: Owner, id: string, change: SetValues & { warmup: boolean }) => editSet(user, id, { ...change, unit: user.units });
export const deleteSet = (user: Owner, id: string) => editSet(user, id, { deletedAt: Date.now() });
